import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { IoMdArrowDropdown } from 'react-icons/io';
import TagSelecionada from './TagSelecionada';

/**
 * Seleção múltipla genérica: escolhe no popover, revê e remove nas tags.
 *
 * POR QUE EXISTE
 *   As telas de dashboard listavam TODAS as opções como chips clicáveis lado a
 *   lado. Com uma dúzia funciona; com cem questionários respondidos a tela vira
 *   uma parede de chips antes de qualquer gráfico — o filtro passa a ocupar
 *   mais espaço que o dado que ele filtra.
 *
 *   Aqui a lista fica GUARDADA no popover e só o que foi escolhido ocupa a
 *   tela, em tags que se removem com um clique. O tamanho da tela passa a
 *   depender do que o usuário escolheu, não de quanto dado existe no sistema.
 *
 * Tem busca, ao contrário do `YearMultiSelect`: a lista de anos é curta por
 * natureza, a de questionários cresce sem teto e rolar cem itens para achar um
 * é o mesmo problema com outra roupa.
 *
 * Popover e não Menu: o Menu do Chakra tem typeahead e rouba o foco do Input a
 * cada tecla — mesma razão do `CompanyMultiSelect`.
 */
const SelecaoMultipla = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Selecione',
  searchPlaceholder = 'Buscar',
  /** Rótulo do botão quando há seleção. Recebe a quantidade. */
  countLabel = (n) => `${n} selecionado${n > 1 ? 's' : ''}`,
  isDisabled = false,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [busca, setBusca] = useState('');
  const buscaRef = useRef(null);

  // Chaves como string: os ids vêm ora number, ora string, dependendo do
  // endpoint — comparar com === entre os dois tipos falha em silêncio e o item
  // nunca aparece como selecionado.
  const selecionados = useMemo(
    () => (value || []).map(String),
    [value]
  );

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return options;
    return options.filter((opcao) =>
      String(opcao.label).toLowerCase().includes(termo)
    );
  }, [options, busca]);

  const porValor = useMemo(
    () => new Map(options.map((opcao) => [String(opcao.value), opcao.label])),
    [options]
  );

  /** Acrescenta as opções informadas às já escolhidas, sem duplicar. */
  const marcarTodas = (opcoes) => {
    const novas = opcoes.map((opcao) => String(opcao.value));
    onChange([...new Set([...selecionados, ...novas])]);
  };

  const alternar = (valor) => {
    const chave = String(valor);
    const proximo = selecionados.includes(chave)
      ? selecionados.filter((atual) => atual !== chave)
      : [...selecionados, chave];
    onChange(proximo);
  };

  if (options.length === 0) return null;

  return (
    <Box>
      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement='bottom-start'
        matchWidth
        isLazy
        // SEM Portal, e isto é o conserto de um bug: este seletor vive
        // DENTRO de um Drawer, e o Drawer do Chakra prende o foco no
        // próprio conteúdo. Painel portalado para fora fica fora dessa
        // trava — o popover abria e não dava para marcar nada.
        // `strategy='fixed'` sozinho já tira o painel do contexto de
        // rolagem, que era a única razão do Portal. Mesma correção que o
        // CompanyMultiSelect já tinha recebido.
        strategy='fixed'
        initialFocusRef={buscaRef}
      >
        <PopoverTrigger>
          <Button
            w='100%'
            h='32px'
            px='12px'
            bg='white'
            border='1px solid'
            borderColor='gray.200'
            borderRadius='8px'
            fontSize='sm'
            fontWeight={500}
            color={selecionados.length ? 'black' : 'gray.500'}
            _hover={{ bg: 'white' }}
            _active={{ bg: 'white' }}
            isDisabled={isDisabled}
            rightIcon={<IoMdArrowDropdown fontSize='20px' opacity={0.5} />}
          >
            <Text flex='1' textAlign='left' noOfLines={1}>
              {selecionados.length ? countLabel(selecionados.length) : placeholder}
            </Text>
          </Button>
        </PopoverTrigger>

          <PopoverContent
            w='auto'
            minW='260px'
            borderRadius='8px'
            _focusVisible={{ outline: 'none' }}
          >
            <PopoverBody p='8px'>
              <Input
                ref={buscaRef}
                size='sm'
                mb='8px'
                borderRadius='6px'
                placeholder={searchPlaceholder}
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
              />

              {/* Marcar tudo age sobre o que está FILTRADO, não sobre o
                  catálogo inteiro: com uma busca ativa, "todos" que ignorasse
                  o filtro traria itens que o usuário não está vendo. */}
              <Flex justify='space-between' align='center' mb='6px' px='2px'>
                <Box
                  as='button'
                  type='button'
                  fontSize='11px'
                  fontWeight={600}
                  color='#0B4A8F'
                  onClick={() => marcarTodas(filtradas)}
                  _hover={{ textDecoration: 'underline' }}
                >
                  {busca.trim() ? 'Marcar os filtrados' : 'Marcar todos'}
                </Box>
                <Box
                  as='button'
                  type='button'
                  fontSize='11px'
                  fontWeight={600}
                  color='#8A94A6'
                  onClick={() => onChange([])}
                  _hover={{ textDecoration: 'underline' }}
                >
                  Limpar
                </Box>
              </Flex>

              {/* Altura limitada: a lista rola dentro do popover em vez de
                  esticar a página até fora da tela. */}
              <Box maxH='260px' overflowY='auto'>
                {filtradas.length === 0 ? (
                  <Text fontSize='sm' color='gray.500' px='8px' py='6px'>
                    Nada encontrado
                  </Text>
                ) : (
                  filtradas.map((opcao) => (
                    <Box
                      key={opcao.value}
                      px='8px'
                      py='6px'
                      borderRadius='6px'
                      _hover={{ bg: '#F4F6F8' }}
                    >
                      <Checkbox
                        isChecked={selecionados.includes(String(opcao.value))}
                        onChange={() => alternar(opcao.value)}
                      >
                        <Text fontSize='sm'>{opcao.label}</Text>
                      </Checkbox>
                    </Box>
                  ))
                )}
              </Box>
            </PopoverBody>
          </PopoverContent>
      </Popover>

      {selecionados.length > 0 && (
        <Flex gap='6px' wrap='wrap' mt='8px'>
          {selecionados.map((chave) => (
            <TagSelecionada
              key={chave}
              label={porValor.get(chave) ?? chave}
              onRemove={() => alternar(chave)}
              isDisabled={isDisabled}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

export default SelecaoMultipla;
